import { useParams, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useGetPayrollEntryQuery } from "@/store/payrolls/apis";
import { Loading } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const formatTime = (dateString: string) => {
  return format(parseISO(dateString), "h:mm a");
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const PayrollEntryDetail = () => {
  const { id, entryId } = useParams<{ id: string; entryId: string }>();
  const { data: entry, isLoading: isEntryLoading } = useGetPayrollEntryQuery({
    id: parseInt(id || "0"),
    entryId: parseInt(entryId || "0"),
  });

  if (isEntryLoading) return <Loading />;
  if (!entry) return <div>Payroll entry not found</div>;

  return (
    <div className="p-6 max-w-screen-lg">
      <div className="mb-6">
        <Link to={`/payroll/${id}`}>
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Payroll
          </Button>
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold">Timesheets for {entry.user.name}</h1>
        {/* <p className="text-gray-600">
          Payroll Period: {format(parseISO(payroll.start_date), "MMM d, yyyy")}{" "}
          - {format(parseISO(payroll.end_date), "MMM d, yyyy")}
        </p> */}
        <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-semibold">Hours Worked:</span>{" "}
            {entry.hours_worked}
          </div>
          <div>
            <span className="font-semibold">Hourly Rate:</span>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(entry.hourly_rate)}
          </div>
          <div>
            <span className="font-semibold">Tips:</span>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(entry.tips)}
          </div>
          <div>
            <span className="font-semibold">Bonuses:</span>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(entry.bonuses)}
          </div>
          <div>
            <span className="font-semibold">Deductions:</span>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(entry.deductions)}
          </div>
          <div>
            <span className="font-semibold">Net Pay:</span>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(entry.net_pay)}
          </div>
        </div>
      </div>

      <div className="text-sm">
        <div className="grid grid-cols-5 gap-4 p-4 font-medium border-b uppercase text-xs text-gray-700">
          <div>Date</div>
          <div>Job/Notes</div>
          <div>Started At</div>
          <div>Ended At</div>
          <div>Duration</div>
        </div>
        {entry.timesheets.map((timesheet) => (
          <div
            key={timesheet.id}
            className="grid grid-cols-5 gap-4 p-4 border-b hover:bg-gray-50"
          >
            <div>{format(parseISO(timesheet.started_at), "MMM d, yyyy")}</div>
            <div>
              {timesheet.move ? (
                <Link
                  to={`/move/${timesheet.move.id}`}
                  className="text-blue-600 hover:underline"
                  target="_blank"
                >
                  {timesheet.move.customer_name}
                </Link>
              ) : (
                timesheet.notes || "-"
              )}
            </div>
            <div>{formatTime(timesheet.started_at)}</div>
            <div>{formatTime(timesheet.ended_at)}</div>
            <div>{formatDuration(timesheet.duration_minutes)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PayrollEntryDetail;
